import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "reactstrap";
import { Check, Search as SearchIcon, X } from "lucide-react";

// Searchable multi-select "attach existing entities" dialog. `candidates` is a
// list of { id, label } NOT yet linked to the target. Pick any number, then
// confirm once — onAdd receives the array of picked ids.
const LinkPicker = ({ open, title, note, verb = "Add", candidates, onAdd, onClose }) => {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(() => new Set());

  // Fresh state every time the dialog opens for a new target.
  useEffect(() => {
    if (open) {
      setQ("");
      setPicked(new Set());
    }
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s
      ? candidates.filter((c) => (c.label || "").toLowerCase().includes(s))
      : candidates;
  }, [q, candidates]);

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Modal isOpen={open} toggle={onClose} centered>
      <div className="cw-picker">
        <div className="cw-picker__head">
          <strong>{title}</strong>
          <button type="button" className="nl-iconbtn" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="nl-search" style={{ flex: "1 1 auto", marginBottom: 10 }}>
          <SearchIcon size={14} className="nl-search__icon" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
        </div>

        <div className="cw-picker__list">
          {filtered.length === 0 ? (
            <div className="cw-picker__empty">Nothing left to add.</div>
          ) : (
            filtered.map((c) => (
              <button
                type="button"
                key={c.id}
                className={`cw-pick ${picked.has(c.id) ? "is-checked" : ""}`}
                aria-pressed={picked.has(c.id)}
                onClick={() => toggle(c.id)}
              >
                <span className="cw-pick__box">
                  <Check size={11} aria-hidden="true" />
                </span>
                <span>{c.label}</span>
              </button>
            ))
          )}
        </div>

        {note && <p className="cw-picker__note">{note}</p>}

        <div className="cw-picker__foot">
          <button type="button" className="nb-btn nb-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="nb-btn nb-btn--primary"
            disabled={picked.size === 0}
            onClick={() => onAdd([...picked])}
          >
            {picked.size === 0 ? verb : `${verb} (${picked.size})`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LinkPicker;
